import { css } from '@emotion/react';
import React, { forwardRef, ReactNode } from 'react';

import {
  borderRadius,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  space,
} from '../shared/styles';
import { InputSize, InputVariant } from './Input.types';

export interface BaseInputProps {
  /**
   * Size variant of the input
   */
  size?: InputSize;
  /**
   * Visual variant of the input
   */
  variant?: InputVariant;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Whether the input is required
   */
  required?: boolean;
  /**
   * Whether the input is read-only
   */
  readOnly?: boolean;
  /**
   * Help text displayed below the input
   */
  helpText?: string;
  /**
   * Error message displayed below the input
   */
  error?: string;
  /**
   * Success message displayed below the input
   */
  success?: string;
  /**
   * Label text for the input
   */
  label?: string;
  /**
   * Whether to hide the label visually (for accessibility)
   */
  hideLabel?: boolean;
  /**
   * Icon to display at the start of the input
   */
  startIcon?: ReactNode;
  /**
   * Icon to display at the end of the input
   */
  endIcon?: ReactNode;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Placeholder text
   */
  placeholder?: string;
}

export interface InputProps
  extends BaseInputProps,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Input type
   */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';
}

const inputStyles = css`
  position: relative;
  width: 100%;
  font-family: ${fonts.base};
  font-weight: ${fontWeights.normal};
  border: 1px solid ${colors.gray['300']};
  border-radius: ${borderRadius.base};
  background-color: ${colors.light['100']};
  color: ${colors.gray['900']};
  transition: all 0.2s ease-in-out;

  &::placeholder {
    color: ${colors.gray['500']};
  }

  &:focus {
    outline: none;
    border-color: ${colors.olive['500']};
    box-shadow: 0 0 0 3px ${colors.olive['100']};
  }

  &:disabled {
    background-color: ${colors.gray['100']};
    color: ${colors.gray['500']};
    cursor: not-allowed;

    &::placeholder {
      color: ${colors.gray['400']};
    }
  }

  &:read-only {
    background-color: ${colors.gray['100']};
    cursor: default;
  }
`;

const sizeStyles = {
  sm: css`
    padding: ${space['2']} ${space['3']};
    font-size: ${fontSizes.sm};
    line-height: ${lineHeights.tight};
    height: ${space['8']};
  `,
  md: css`
    padding: ${space['3']} ${space['4']};
    font-size: ${fontSizes.base};
    line-height: ${lineHeights.normal};
    height: ${space['10']};
  `,
  lg: css`
    padding: ${space['4']} ${space['5']};
    font-size: ${fontSizes.lg};
    line-height: ${lineHeights.normal};
    height: ${space['12']};
  `,
};

const variantStyles = {
  default: css``,
  error: css`
    border-color: ${colors.red['500']};

    &:focus {
      border-color: ${colors.red['500']};
      box-shadow: 0 0 0 3px ${colors.red['100']};
    }
  `,
  success: css`
    border-color: ${colors.accent.green};

    &:focus {
      border-color: ${colors.accent.green};
      box-shadow: 0 0 0 3px rgba(76, 168, 23, 0.1);
    }
  `,
};

const inputWithIconStyles = css`
  padding-left: ${space['10']};

  &.has-end-icon {
    padding-right: ${space['10']};
  }

  &.has-start-icon {
    padding-left: ${space['10']};
  }
`;

const iconContainerStyles = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.gray['500']};
  pointer-events: none;
  z-index: 1;
`;

const startIconStyles = css`
  ${iconContainerStyles}
  left: ${space['3']};
`;

const endIconStyles = css`
  ${iconContainerStyles}
  right: ${space['3']};
`;

const inputWrapperStyles = css`
  position: relative;
  display: inline-block;
  width: 100%;
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      variant = 'default',
      type = 'text',
      disabled = false,
      readOnly = false,
      startIcon,
      endIcon,
      className = '',
      ...props
    },
    ref
  ) => {
    const hasStartIcon = !!startIcon;
    const hasEndIcon = !!endIcon;

    const inputClasses = [
      hasStartIcon && 'has-start-icon',
      hasEndIcon && 'has-end-icon',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div css={inputWrapperStyles}>
        {hasStartIcon && <div css={startIconStyles}>{startIcon}</div>}

        <input
          ref={ref}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          css={[
            inputStyles,
            sizeStyles[size],
            variantStyles[variant],
            (hasStartIcon || hasEndIcon) && inputWithIconStyles,
          ]}
          className={inputClasses}
          {...props}
        />

        {hasEndIcon && <div css={endIconStyles}>{endIcon}</div>}
      </div>
    );
  }
);

Input.displayName = 'Input';
